import { useCallback, useRef, useState } from "react";
import { useNara, type NaraBlock } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import { usePages } from "@/data/hooks";
import { streamChat } from "@/data/chat";

export interface NaraEngine {
  streaming: boolean;
  reply: string;
  blocks: NaraBlock[];
  send: (prompt: string) => void;
  reset: () => void;
  /** true when streaming from the real ai-agent (/ai/v1/chat) */
  live: boolean;
}

/**
 * Unified Nara engine. When the workspace is live AND the current project has a
 * real page, prompts stream from the real ai-agent over SSE; otherwise it falls
 * back to the polished local simulation (so Make always works offline).
 *
 * The chosen path is LOCKED per conversation (`usedLive`) so an async pages
 * query resolving mid-stream can't swap the source and drop the reply.
 *
 * Live proposals are RFC-6902 tree patches against the page's component tree;
 * applying them to the preview needs the page-editor port, so live mode surfaces
 * Nara's real reasoning + proposal rationale and leaves block-application next.
 */
export function useNaraEngine(): NaraEngine {
  const mock = useNara();
  const { currentProjectId, live } = useWorkspace();
  const pages = usePages(currentProjectId);
  const pageId = pages.data?.[0]?.id;
  const canLive = live && !!pageId;

  const [usedLive, setUsedLive] = useState<boolean | null>(null);
  const [liveStreaming, setLiveStreaming] = useState(false);
  const [liveReply, setLiveReply] = useState("");
  const abort = useRef<AbortController | null>(null);

  const send = useCallback(
    (prompt: string) => {
      if (canLive && pageId) {
        setUsedLive(true);
        abort.current?.abort();
        const ac = new AbortController();
        abort.current = ac;
        setLiveReply("");
        setLiveStreaming(true);
        let acc = "";
        const push = (s: string) => {
          acc += (acc ? "\n\n" : "") + s;
          setLiveReply(acc);
        };
        streamChat(
          { page_id: pageId, message: prompt, registry_spec: [] },
          (e) => {
            if (e.type === "thinking" && typeof e.data === "string") push(e.data);
            else if (e.type === "proposal") {
              const r = (e.data as { rationale?: string } | null)?.rationale;
              if (r) push("💡 " + r);
            } else if (e.type === "error") push("(Nara hit an error.)");
          },
          ac.signal,
        )
          .catch(() => push("(Couldn't reach Nara just now.)"))
          .finally(() => setLiveStreaming(false));
      } else {
        setUsedLive(false);
        mock.send(prompt);
      }
    },
    [canLive, pageId, mock],
  );

  const reset = useCallback(() => {
    if (usedLive) {
      abort.current?.abort();
      setLiveReply("");
      setLiveStreaming(false);
    } else {
      mock.reset();
    }
    setUsedLive(null);
  }, [usedLive, mock]);

  // Before sending, reflect whether a live path is available; after sending,
  // reflect the path actually used.
  const isLive = usedLive ?? canLive;
  if (usedLive === true) {
    return { streaming: liveStreaming, reply: liveReply, blocks: [], send, reset, live: true };
  }
  if (usedLive === false) {
    return { streaming: mock.streaming, reply: mock.reply, blocks: mock.blocks, send, reset, live: false };
  }
  return { streaming: false, reply: "", blocks: [], send, reset, live: isLive };
}
